import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAppStore } from "@/store/app-store";
import type { components } from "@/api/types";
import { trackEvent, AnalyticsEvent } from "@/analytics";

/**
 * SOP/SOG document pipeline (RES-57/58): upload → confirm → extract → review.
 *
 * Upload is the backend's two-phase presigned-PUT flow: POST a ticket (row is
 * created `pending_upload` + a presigned S3 URL comes back), PUT the bytes to
 * S3 with a RAW fetch — the presigned signature locks the Content-Type, and
 * the S3 endpoint must NOT receive our Authorization / X-RespAi headers — then
 * POST confirm so the server verifies the object really landed (head_object)
 * and flips the row to `uploaded`.
 *
 * Every queryKey carries the active department id so a respai_admin switching
 * tenants never sees another department's documents or rules from cache.
 */

export type SopDocument = components["schemas"]["SopDocumentRead"];
export type SopRule = components["schemas"]["SopRuleRead"];
export type SopRuleUpdate = components["schemas"]["SopRuleUpdate"];
export type SopExtractionSummary = components["schemas"]["SopExtractionSummary"];
type SopDocumentCreateResponse = components["schemas"]["SopDocumentCreateResponse"];

/** Server-side cap (25 MB), mirrored client-side for a friendly early error. */
export const MAX_SOP_DOCUMENT_BYTES = 25_000_000;

/** Content types the ticket endpoint accepts (PDF, Word, plain text). */
export const ALLOWED_SOP_CONTENT_TYPES: readonly string[] = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "text/plain",
];

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  txt: "text/plain",
};

/**
 * Resolve the content type for a picked file. Browsers usually populate
 * `file.type`, but some leave it blank (notably for .txt/.doc from network
 * shares) — fall back to the extension so the ticket request stays honest.
 */
export function sopContentType(file: { name: string; type?: string }): string | null {
  if (file.type && ALLOWED_SOP_CONTENT_TYPES.includes(file.type)) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_CONTENT_TYPES[ext] ?? null;
}

/** How often the documents list is re-fetched while an extraction runs. */
export const EXTRACTION_POLL_INTERVAL_MS = 5_000;

/**
 * Server-side stale window. The server lets a new run take over an
 * `extracting` claim older than this, and stops a live run well inside it (at
 * 24 minutes plus one in-flight model call), so past this age a claim is
 * stranded, not slow. One window decides both polling and retry: a document
 * is either running (poll, no retry) or stranded (retry, no poll), never both.
 */
export const EXTRACTION_STALE_AFTER_MS = 30 * 60_000;

/**
 * When this page first saw each extraction claim, on the browser's own clock.
 *
 * A claim is stranded when either of two things says so:
 *
 * - The server, through `extraction_stale`, computed on its own clock with
 *   the same rule its takeover uses. This covers a page load: a claim that
 *   stalled hours ago is stranded at once, not 30 minutes after the load.
 * - This page, once it has watched the claim for the full window. This is a
 *   fallback for a claim the page keeps watching; polling usually picks up
 *   the server's verdict first.
 *
 * The page never compares the browser's clock with the server's
 * `extraction_requested_at`: a browser clock running fast would call a fresh
 * claim stranded, stop polling, and offer a retry the server refuses with a
 * 409. Measured from first sight, the page can only wait longer than the
 * server does, never less.
 */
const claimFirstSeenAt = new Map<string, number>();

/** Forget every observed claim (tests). */
export function resetExtractionClaimClock(): void {
  claimFirstSeenAt.clear();
}

type ClaimFields = Pick<
  SopDocument,
  "id" | "status" | "extraction_requested_at" | "extraction_stale"
>;

/**
 * Milliseconds until a running claim counts as stranded, or null when the
 * document has no running claim (not `extracting`, or already stranded).
 */
export function extractionClaimMsLeft(
  doc: ClaimFields,
  now: number = Date.now(),
  firstSeenAt: Map<string, number> = claimFirstSeenAt,
): number | null {
  if (doc.status !== "extracting") return null;
  // A claim with no time can be taken over by any request (the server agrees).
  if (!doc.extraction_requested_at) return null;
  // The server would take this claim over now, however recently the page
  // first saw it.
  if (doc.extraction_stale) return null;
  const key = `${doc.id}|${doc.extraction_requested_at}`;
  let seenAt = firstSeenAt.get(key);
  if (seenAt === undefined) {
    seenAt = now;
    firstSeenAt.set(key, now);
  }
  const left = seenAt + EXTRACTION_STALE_AFTER_MS - now;
  return left > 0 ? left : null;
}

export type ExtractionClaim = "none" | "running" | "stranded";

/** The document's extraction claim: none, running (poll), or stranded (retry). */
export function extractionClaim(
  doc: ClaimFields,
  now: number = Date.now(),
  firstSeenAt: Map<string, number> = claimFirstSeenAt,
): ExtractionClaim {
  if (doc.status !== "extracting") return "none";
  return extractionClaimMsLeft(doc, now, firstSeenAt) === null ? "stranded" : "running";
}

/** refetchInterval for the documents list: poll only while a run is in flight. */
export function documentsRefetchInterval(
  docs: SopDocument[] | undefined,
  now: number = Date.now(),
  firstSeenAt: Map<string, number> = claimFirstSeenAt,
): number | false {
  return docs?.some((doc) => extractionClaim(doc, now, firstSeenAt) === "running")
    ? EXTRACTION_POLL_INTERVAL_MS
    : false;
}

/** One line summarizing the last successful extraction run. */
export function extractionSummaryText(summary: SopExtractionSummary): string {
  const parts = [
    `${summary.rules_inserted} new`,
    `${summary.rules_updated} updated`,
  ];
  if (summary.rules_renamed_for_reviewed > 0) {
    parts.push(`${summary.rules_renamed_for_reviewed} renamed to avoid reviewed codes`);
  }
  if (summary.rules_skipped_reviewed > 0) {
    parts.push(`${summary.rules_skipped_reviewed} already reviewed`);
  }
  if ((summary.rules_pruned ?? 0) > 0) {
    parts.push(`${summary.rules_pruned} removed`);
  }
  return `Last run: ${parts.join(" · ")}`;
}

export function useSopDocuments() {
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["sop-documents", deptId],
    queryFn: () => api.get<SopDocument[]>("/sop/documents"),
    refetchInterval: (query) => documentsRefetchInterval(query.state.data),
  });
}

/** A rule is pending review while the machine wrote it (or a reviewer edited it). */
export function isPendingRule(rule: Pick<SopRule, "status">): boolean {
  return rule.status === "extracted" || rule.status === "edited";
}

export function useSopRules(documentId?: string) {
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["sop-rules", deptId, documentId ?? "all"],
    queryFn: () =>
      api.get<SopRule[]>("/sop/rules", {
        query: documentId ? { document_id: documentId } : undefined,
      }),
  });
}

/**
 * Three-phase upload: ticket → raw PUT to S3 → confirm. Fails loudly at the
 * phase that broke so the inline error names the actual problem.
 */
export function useUploadSopDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<SopDocument> => {
      const contentType = sopContentType(file);
      if (!contentType) {
        throw new Error("Unsupported file type — upload a PDF, Word document, or plain text.");
      }
      if (file.size < 1 || file.size > MAX_SOP_DOCUMENT_BYTES) {
        throw new Error("File must be between 1 byte and 25 MB.");
      }

      const ticket = await api.post<SopDocumentCreateResponse>("/sop/documents", {
        filename: file.name,
        content_type: contentType,
        size_bytes: file.size,
      });

      // RAW fetch: the presigned URL is its own authorization. Sending the API
      // client's Authorization / X-RespAi headers would break the S3 signature.
      const put = await fetch(ticket.upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": contentType },
      });
      if (!put.ok) {
        throw new Error(`Upload to storage failed (HTTP ${put.status}).`);
      }

      return api.post<SopDocument>(`/sop/documents/${ticket.document.id}/confirm`);
    },
    onSuccess: (_doc, file) => {
      qc.invalidateQueries({ queryKey: ["sop-documents"] });
      // No filename in analytics — it could identify a department's internal docs.
      trackEvent(AnalyticsEvent.SopDocumentUploaded, {
        size_kb: Math.round(file.size / 1024),
      });
    },
  });
}

/**
 * Starts a background extraction. The server answers 202 with the document at
 * `extracting`; the documents query then polls until it settles on
 * `extracted` (counts in `last_extraction`) or `failed` (`error_message`).
 */
export function useExtractSopDocument() {
  const qc = useQueryClient();
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useMutation({
    mutationFn: (documentId: string) =>
      api.post<SopDocument>(`/sop/documents/${documentId}/extract`),
    onSuccess: (claimed) => {
      // Show `extracting` at once, which also switches polling on.
      qc.setQueryData<SopDocument[]>(["sop-documents", deptId], (docs) =>
        docs?.map((doc) => (doc.id === claimed.id ? claimed : doc)),
      );
      qc.invalidateQueries({ queryKey: ["sop-documents"] });
    },
  });
}

function invalidateRuleConsumers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["sop-rules"] });
  // Approvals/rejections change what the compliance report scores.
  qc.invalidateQueries({ queryKey: ["sop-compliance"] });
}

export function useEditSopRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, edits }: { ruleId: string; edits: SopRuleUpdate }) =>
      api.patch<SopRule>(`/sop/rules/${ruleId}`, edits),
    onSuccess: () => invalidateRuleConsumers(qc),
  });
}

export function useApproveSopRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rule: Pick<SopRule, "id" | "category">) =>
      api.post<SopRule>(`/sop/rules/${rule.id}/approve`),
    onSuccess: (_rule, variables) => {
      invalidateRuleConsumers(qc);
      // Category only — rule/document UUIDs never reach analytics.
      trackEvent(AnalyticsEvent.SopRuleApproved, { category: variables.category });
    },
  });
}

export function useRejectSopRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      rule,
      review_note,
    }: {
      rule: Pick<SopRule, "id" | "category">;
      review_note?: string;
    }) =>
      api.post<SopRule>(`/sop/rules/${rule.id}/reject`, {
        review_note: review_note || null,
      }),
    onSuccess: (_rule, variables) => {
      invalidateRuleConsumers(qc);
      trackEvent(AnalyticsEvent.SopRuleRejected, { category: variables.rule.category });
    },
  });
}
